import { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
  useMediaQuery,
  Link,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core";
import ProjectPoolHeading from "../Projects/ProjectPoolHeading";
import cross from "../../assets/images/cross.svg";
import useStyles from "../Styles/ProjectModalStyles";
import { ContributionModalProps } from "../../interfaces";
import WarningIcon from "../../assets/images/WarningIcon.svg";
import ExpireLink from "../../assets/images/ExpireLinkIcon.svg";
import ContributionModalStepper from "./ContributionModalStepper";
import WalletContext from "../../store/Wallet/WalletContext";
import AlertContext from "../../store/Alert/AlertContext";
import graphQLClient from "../../utils/authHeader";

import { ChainName, NetworkId } from "../../store/Wallet/WalletFunction";

import GET_POOL_WHITELIST_STATE from "../../utils/queries/pool/getPoolWhitelistState";
import CREATE_INVESTMENT_INTENT from "../../utils/mutations/invest/createInvestmentIntent";
import CONFIRM_INVESTMENT_INTENT from "../../utils/mutations/invest/confirmInvestmentIntent";
import REMOVE_INVESTMENT_INTENT from "../../utils/mutations/invest/removeInvestmentIntent";
import { useAccount } from "wagmi";
import { CallExecutionError, TransactionExecutionError, WaitForTransactionReceiptTimeoutError } from "viem";
import { shorter } from "../../utils/web3/formatBalance";
import { Contracts } from "../../config/contracts";
import { LowGasError } from "../../services/web3";

const ContributionModal: FC<ContributionModalProps> = ({
  open,
  setOpen,
  contributionCurrency,
  contributionAmount,
  contractAddress,
  tokenAddress,
  poolId,
  networkId,
  maxAllowed,
  usdPerTicketValue,
  handleContribution,
  setContributionCardState,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isActive = useMediaQuery(theme.breakpoints.down("xs"));
  const isActiveSM = useMediaQuery(theme.breakpoints.only("sm"));

  const { approveTokenSpent, contribute, fetchTokenAllowance } =
    useContext<any>(WalletContext);
  const { setAlert } = useContext<any>(AlertContext);

  const [active, setActive] = useState(false);
  const [contributing, setContributing] = useState(false);
  const [approveEnabled, setApproveEnabled] = useState(false);
  const [canContribute, setCanContribute] = useState(false);
  const [poolData, setPoolData] = useState<any>();
  const [intentData, setIntentData] = useState<any>();
  const [contributed, setContributed] = useState(false);
  const [allowance, setAllowance] = useState();
  const [fetchingAllowance, setFetchingAllowance] = useState(true);
  const [spender, setSpender] = useState<any>();
  const { address, chain } = useAccount();

  // Fetch pool whitelist data
  useEffect(() => {
    const variables = {
      poolId: poolId,
      networkId: networkId,
    };

    graphQLClient
      .request(GET_POOL_WHITELIST_STATE, variables)
      .then((data: any) => {
        setPoolData(data.getPoolWhitelistState);
      })
      .catch((error: any) => {
        setOpen(false);
        setAlert("Unable to fetch pool whitelist state.", "error");
      });
  }, []);

  useEffect(() => {
    setSpender(Contracts.proxy[networkId])
  }, [networkId]);

  useEffect(() => {
    if (poolData && spender) {
      let signer = poolData?.address;
      fetchTokenAllowance(networkId, tokenAddress, spender, signer).then(
        (allowance: any) => {
          setAllowance(allowance);
          setFetchingAllowance(false);
        }
      );
    }
  }, [poolData, spender, chain]);

  useEffect(() => {
    if (intentData && poolData) {
      contribute(
        networkId,
        Number(contributionAmount),
        poolData.proof,
        contractAddress,
        tokenAddress,
        Number(poolData.whitelist),
        poolData.address
      )
        .then((transactionHash: any) => {
          setAlert(
            "Successfully contributed to the project on chain.",
            "success"
          );
          setContributing(false);
          setCanContribute(true);

          const confirmVariable = {
            investmentIntentId: intentData.id,
            trxHash: transactionHash,
          };

          graphQLClient
            .request(CONFIRM_INVESTMENT_INTENT, confirmVariable)
            .then((data: any) => {
              setContributed(true);
              handleContribution();
              setOpen(false);
              setContributionCardState('investSuccess');
            })
            .catch((error: any) => {
              setAlert(
                "Unable to capture investment off chain, it will be updated soon.",
                "info"
              );
              setContributed(true);
              handleContribution();
              setOpen(false);
              setContributionCardState('investPendingConfirmation');
            });
        })
        .catch((error: any) => {
          setContributing(false);
          setCanContribute(true);
          if (error instanceof TransactionExecutionError) setAlert("User rejected contribution transaction request. Please accept to proceed with investment", "error");
          else if (error instanceof LowGasError) {
            setAlert("Transaction failed due to low gas. Please try again.", "error");
            setContributionCardState('investFail');
          }
          else if (error instanceof CallExecutionError) { 
            setAlert("The transaction failed. Verify with the progress bar that the amount is valid");
            setContributionCardState('investFail');
          } else {
            setAlert("Unable to contribute at the moment, please try again later.", "error");
            setContributionCardState('investChainError');
          }
        });
    }
  }, [intentData]);

  useEffect(() => {
    if (Number(allowance) >= contributionAmount) {
      setApproveEnabled(false);
      setCanContribute(true);
    } else {
      setApproveEnabled(true);
      setCanContribute(false);
    }
  }, [allowance]);

  const Expire = () => {
    return <img src={ExpireLink} className="img-fluid " alt="Gains" />;
  };

  const Warning = () => {
    return (
      <img src={WarningIcon} className="img-fluid warningimg" alt="warning" />
    );
  };

  const handleClose = () => {
    // Remove intent if modal closed
    // and user has not contributed.
    if (canContribute && intentData && !contributed) {
      const variables = {
        investmentIntentId: intentData.id,
      };
      graphQLClient
        .request(REMOVE_INVESTMENT_INTENT, variables)
        .then((data) => {
          console.info(data);
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
    setOpen(false);
  };

  const handleApproved = () => {
    setActive(true);
    approveTokenSpent(
      networkId,
      Number(contributionAmount),
      tokenAddress,
      spender
    )
      .then((transactionHash: any) => {
        setAlert("Successfully approved token spending.", "success");
        setActive(false);
        setCanContribute(true);
        setApproveEnabled(false);
      })
      .catch((error: any) => {
        if (error instanceof TransactionExecutionError) setAlert("User rejected approval transaction request. Please approve to proceed with investment", "error");
        else {
          setAlert("Unable to approve transaction at the moment, please try again later.", "error");
        }
        setActive(false);
      });
  };

  const handleContribute = () => {
    setCanContribute(false);
    setContributing(true);

    const network = NetworkId(networkId);

    const create_investment_variables = {
      poolId: poolId,
      networkId: network,
      walletAddress: address,
      amount: Number(contributionAmount),
    };

    graphQLClient
      .request(CREATE_INVESTMENT_INTENT, create_investment_variables)
      .then((data: any) => {
        setIntentData(data.createInvestmentIntent);
      })
      .catch((error: any) => {
        setAlert(
          "Unable to create investment intent, please try again.",
          "error"
        );
        setCanContribute(true);
        setContributed(false);
      });
  };

  return (
    <>
      <Box component="section">
        <Dialog
          open={open}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className={` ${classes.modalpages} ${classes.contributeModal}`}
          fullScreen={isActive}
        >
          <Box
            ml={isActive ? "20px" : 7}
            my={isActive ? "30px" : 7}
            mr={isActive ? "20px" : 3}
            className="relative-box"
          >
            <img
              src={cross}
              alt=""
              className="cross-icon"
              onClick={handleClose}
            />

            <ProjectPoolHeading title1="Contribute" title2="" />
            <Box display="flex" alignItems={isActiveSM ? "center" : ""}>
              <Box>
                <Warning />
              </Box>
              <Box ml={1}>
                <Typography
                  variant="subtitle1"
                  className="text-darkslate fw-normal warning-text"
                >
                  Please don't leave the page and wait until the status changes
                  or your <br /> transaction might fail.
                </Typography>
              </Box>
            </Box>
            <Box mt={2}>
              <Typography
                variant="body1"
                className="text-darkslate warning-text "
              >
                Below is your whitelisted wallet, you can only contribute <br />{" "}
                using this address.
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Whitelisted Wallet
              </Typography>
              <Typography
                variant="body1"
                color="secondary"
                className="mt-8 text-darkslate"
              >
                {" "}
                {
                  poolData?.address
                    ?
                      isActive
                        ? poolData?.address.length > 5
                          ? poolData?.address.substr(0, 6) +
                            "..." +
                            poolData?.address.substr(
                              poolData?.address.length - 4,
                              poolData?.address.length
                            )
                          : poolData?.address
                        : poolData?.address
                    : "Loading..."
                }
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Network
              </Typography>
              <Typography
                variant="body1"
                color="secondary"
                className="mt-8 text-darkslate"
              >
                {ChainName(networkId)}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Contribution Address
              </Typography>
              <Typography
                variant="body1"
                color="secondary"
                className="mt-8 text-darkslate"
              >
                { isActive ? shorter(contractAddress) : contractAddress }
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Currency
              </Typography>
              <Typography
                variant="body1"
                color="secondary"
                className="mt-8 text-darkslate"
              >
                {contributionCurrency}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Tickets
              </Typography>
              <Typography
                variant="body1"
                color="secondary"
                className="mt-8 text-darkslate"
              >
                {Math.floor(maxAllowed/usdPerTicketValue)} Winning Ticket(s)
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body1" color="secondary" className="fw-bold">
                Contribution Amount
                <Typography
                  component="span"
                  variant="subtitle1"
                  color="secondary"
                  className="fw-normal ml-8"
                >
                  Max Individual cap:{" "}
                  <Typography
                    component="span"
                    variant="subtitle1"
                    color="primary"
                    className="fw-bold"
                  >
                    {maxAllowed}
                  </Typography>
                </Typography>
              </Typography>
              <Typography variant="body1" color="secondary" className="mt-8 ">
                {(contributionAmount)}
              </Typography>
            </Box>

            <Box mt={2} display="flex" alignItems={isActive ? "" : "center"}>
              <Box>
                <Expire />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  className="fw-normal ml-12 font-family-Poppin"
                >
                  By clicking “Contribute” you agree to{" "}
                  <Link
                    href="https://www.gains-associates.com/#/terms-conditions"
                    target="_blank"
                    rel="noopener"
                    className="text-decoration-none"
                  >
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      className=" fw-bold"
                      component="span"
                    >
                      Terms and Conditions
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>

            <Box display="flex" mt={isActive ? 3 : 5}>
              <Box mr={isActive ? 0 : 1} flexGrow={isActive ? 1 : 0}>
                <Button
                  variant="contained"
                  color="secondary"
                  className={`contribute-btn  ${active ? "action" : ""}`}
                  onClick={handleApproved}
                  disabled={!approveEnabled || fetchingAllowance}
                >
                  {(active || fetchingAllowance) && (
                    <CircularProgress
                      className="m-class mr-8"
                      variant="indeterminate"
                      size={15}
                      thickness={2}
                      color="secondary"
                    />
                  )}{" "}
                  {fetchingAllowance
                    ? "Fetching..."
                    : `Approve ${contributionCurrency}`}
                </Button>
              </Box>
              <Box ml={isActive ? 0 : 1} flexGrow={isActive ? 1 : 0}>
                <Button
                  variant="contained"
                  color="secondary"
                  className={`contribute-btn  ${contributing ? "action" : ""}`}
                  disabled={!canContribute || chain?.id !== networkId || contributing }
                  onClick={handleContribute}
                >
                  {contributing && (
                    <CircularProgress
                      className="m-class mr-8"
                      variant="indeterminate"
                      size={15}
                      thickness={2}
                      color="secondary"
                    />
                  )}{" "}
                  {
                    (chain?.id !== networkId) 
                      ? "Incorrect network"
                      : contributing
                        ? "Contributing"
                        : "Contribute"
                  }
                </Button>
              </Box>
            </Box>
            <Box mt={4} display="flex" justifyContent="center">
              <ContributionModalStepper />
            </Box>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default ContributionModal;
